import { useState, useEffect, useCallback, ReactNode } from 'react';
import toast from 'react-hot-toast';
import {
	DocumentTextIcon,
	MagnifyingGlassIcon,
	ExclamationTriangleIcon,
	CheckCircleIcon,
	ClockIcon,
	XCircleIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Alert, AppLoader, Button, Modal, Spinner } from '../../components/ui';
import { providerProfileApi, ApiError } from '../../api';
import useProviderProfileStore from '../../stores/providerProfileStore';
import type {
	ProviderTcr,
	ProviderTcrResponse,
	ProviderTcrStaffOption,
} from '../../types/providerTcr';

// ─── Constants ──────────────────────────────────────────────────

const STATUS_STYLES: Record<
	string,
	{ border: string; badge: string; banner: string; icon: string }
> = {
	PENDING: {
		border: 'border-l-amber-500',
		badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
		banner: 'bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800',
		icon: 'bg-amber-100 dark:bg-amber-900/40',
	},
	APPROVED: {
		border: 'border-l-emerald-500',
		badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
		banner: 'bg-emerald-50 border border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800',
		icon: 'bg-emerald-100 dark:bg-emerald-900/40',
	},
	REJECTED: {
		border: 'border-l-red-500',
		badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
		banner: 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800',
		icon: 'bg-red-100 dark:bg-red-900/40',
	},
};

const STATUS_OPTIONS = [
	{ value: 'ALL', label: 'All Status' },
	{ value: 'PENDING', label: 'Pending' },
	{ value: 'APPROVED', label: 'Approved' },
	{ value: 'REJECTED', label: 'Rejected' },
];

// ─── Helpers ────────────────────────────────────────────────────

const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
};

const formatDate = (dateStr: string): string => {
	try {
		const date = new Date(dateStr);
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	} catch {
		return dateStr;
	}
};

const formatTaskType = (taskType: string): string => {
	return taskType.charAt(0).toUpperCase() + taskType.slice(1).toLowerCase();
};

const getDateParts = (dateStr: string): { day: string; month: string } => {
	try {
		const date = new Date(dateStr);
		return {
			day: date.getDate().toString(),
			month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
		};
	} catch {
		return { day: '-', month: '-' };
	}
};

const getStatusStyle = (status: string) => STATUS_STYLES[status] || STATUS_STYLES.PENDING;

const StatusIcon = ({ status, className }: { status: string; className?: string }) => {
	const iconClass = className || 'w-5 h-5';
	switch (status) {
		case 'APPROVED':
			return <CheckCircleIcon className={iconClass} />;
		case 'REJECTED':
			return <XCircleIcon className={iconClass} />;
		default:
			return <ClockIcon className={iconClass} />;
	}
};

// ─── TCR Detail Modal ───────────────────────────────────────────

interface TCRDetailModalProps {
	tcr: ProviderTcr | null;
	onClose: () => void;
	onApprove: (id: number) => void;
	onReject: (id: number, notes: string) => void;
	isApproving: boolean;
	isRejecting: boolean;
}

const TCRDetailModal = ({
	tcr,
	onClose,
	onApprove,
	onReject,
	isApproving,
	isRejecting,
}: TCRDetailModalProps) => {
	const [showRejectForm, setShowRejectForm] = useState(false);
	const [rejectionNotes, setRejectionNotes] = useState('');

	// Reset rejection form when modal opens/closes or TCR changes
	useEffect(() => {
		setShowRejectForm(false);
		setRejectionNotes('');
	}, [tcr]);

	if (!tcr) return null;

	const style = getStatusStyle(tcr.status);
	const isPending = tcr.status === 'PENDING';

	const handleReject = () => {
		if (!rejectionNotes.trim()) {
			toast.error('Please provide a reason for rejection');
			return;
		}
		onReject(tcr.id, rejectionNotes.trim());
	};

	const headerContent = (
		<div
			className="px-6 py-5 flex items-center gap-4"
			style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
		>
			<div className="flex-1 min-w-0">
				<h2 className="text-lg font-bold text-white truncate">Task Completion Report</h2>
				<p className="text-sm text-white/80 mt-0.5 truncate">
					{tcr.serviceName} &middot; {formatDate(tcr.appointmentDateTime)}
				</p>
			</div>
		</div>
	);

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title=""
			size="xl"
			headerContent={headerContent}
			panelClassName="max-h-[90vh] flex flex-col"
			bodyClassName="!p-0 flex-1 flex flex-col min-h-0"
		>
			<div className="flex-1 overflow-y-auto p-6 space-y-6">
				{/* Status Banner */}
				<div className={`flex items-center gap-3 p-4 rounded-xl ${style.banner}`}>
					<div
						className={`w-10 h-10 rounded-lg flex items-center justify-center ${style.icon}`}
					>
						<StatusIcon status={tcr.status} className="w-5 h-5" />
					</div>
					<div>
						<p className="font-semibold text-gray-900 dark:text-white">
							{tcr.status === 'APPROVED'
								? 'Approved'
								: tcr.status === 'REJECTED'
									? 'Rejected'
									: 'Pending Review'}
						</p>
						<p className="text-sm text-gray-500 dark:text-gray-400">
							Submitted {formatDate(tcr.submittedAt)}
						</p>
					</div>
				</div>

				{/* Staff Information */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Staff Information
					</h3>
					<div className="grid grid-cols-2 gap-3">
						<div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
							<p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
								Staff Name
							</p>
							<p className="text-sm font-semibold text-gray-900 dark:text-white">
								{tcr.staffName}
							</p>
						</div>
						<div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
							<p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
								Role
							</p>
							<p className="text-sm font-semibold text-gray-900 dark:text-white">
								{tcr.staffRole}
							</p>
						</div>
					</div>
				</div>

				{/* Service Details */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Service Details
					</h3>
					<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
						{[
							{ label: 'Client', value: tcr.customerName },
							{ label: 'Service', value: tcr.serviceName },
							{ label: 'Size', value: tcr.size || '-' },
							{ label: 'Length', value: tcr.hairLength || '-' },
							{
								label: 'Duration',
								value: tcr.durationMinutes ? `${tcr.durationMinutes} mins` : '-',
							},
							{ label: 'Service Price', value: formatCurrency(tcr.amount) },
						].map(item => (
							<div
								key={item.label}
								className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg"
							>
								<p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
									{item.label}
								</p>
								<p className="text-sm font-semibold text-gray-900 dark:text-white">
									{item.value}
								</p>
							</div>
						))}
					</div>
				</div>

				{/* Tasks Performed */}
				{tcr.taskCompletions && tcr.taskCompletions.length > 0 && (
					<div>
						<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
							Tasks Performed
						</h3>
						<div className="space-y-3">
							{tcr.taskCompletions.map((task, idx) => {
								const pctDisplay = Math.round(task.completionPct * 100);
								return (
									<div
										key={idx}
										className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg"
									>
										<div
											className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
											style={{
												background:
													'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
											}}
										>
											{task.taskType.charAt(0)}
										</div>
										<div>
											<p className="font-semibold text-gray-900 dark:text-white">
												{formatTaskType(task.taskType)}
											</p>
											<p className="text-sm text-gray-500 dark:text-gray-400">
												{pctDisplay}% completion
											</p>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* Materials Used */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Materials Used
					</h3>
					{tcr.materials && tcr.materials.length > 0 ? (
						<div className="space-y-2">
							{tcr.materials.map((material, idx) => (
								<div
									key={idx}
									className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg"
								>
									<div>
										<p className="text-sm font-medium text-gray-900 dark:text-white">
											{material.materialName ||
												formatTaskType(material.materialType)}
										</p>
										{material.length && (
											<p className="text-xs text-gray-500 dark:text-gray-400">
												Length: {material.length}
											</p>
										)}
									</div>
									<div className="text-right">
										<p className="text-sm font-medium text-gray-900 dark:text-white">
											{material.packs} pack{material.packs !== 1 ? 's' : ''}
										</p>
										<p className="text-xs text-gray-500 dark:text-gray-400">
											{formatCurrency(material.price)}
										</p>
									</div>
								</div>
							))}
						</div>
					) : (
						<p className="text-sm text-gray-400 dark:text-gray-500 italic">
							No materials used
						</p>
					)}
				</div>

				{/* Tip */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Tip from Customer
					</h3>
					{tcr.tip > 0 ? (
						<div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-4 rounded-lg">
							<span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
								{formatCurrency(tcr.tip)}
							</span>
						</div>
					) : (
						<p className="text-sm text-gray-400 dark:text-gray-500 italic">
							No tip received
						</p>
					)}
				</div>

				{/* Work Notes */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Work Notes
					</h3>
					{tcr.notes ? (
						<div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border-l-3 border-[#667eea]">
							<p className="text-sm text-gray-700 dark:text-gray-300">{tcr.notes}</p>
						</div>
					) : (
						<p className="text-sm text-gray-400 dark:text-gray-500 italic">
							No work notes
						</p>
					)}
				</div>

				{/* Photos */}
				{tcr.workPhotosBase64 && tcr.workPhotosBase64.length > 0 && (
					<div>
						<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
							Completed Work Photos
						</h3>
						<div className="grid grid-cols-2 gap-3">
							{tcr.workPhotosBase64.map((photo, idx) => (
								<div
									key={idx}
									className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 aspect-square"
								>
									<img
										src={
											photo.startsWith('data:')
												? photo
												: `data:image/jpeg;base64,${photo}`
										}
										alt={`Work photo ${idx + 1}`}
										className="w-full h-full object-cover"
									/>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Cost Summary */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Cost Summary
					</h3>
					<div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 p-5">
						{/* Service Price */}
						<div className="flex justify-between items-center py-2.5">
							<span className="text-[0.95rem] text-gray-500 dark:text-gray-400">
								Service Price
							</span>
							<span className="font-semibold text-gray-900 dark:text-gray-100">
								{formatCurrency(tcr.amount)}
							</span>
						</div>

						{/* Materials Cost */}
						<div className="text-[0.95rem] font-semibold text-emerald-500 dark:text-emerald-400 mt-4 mb-1 pb-2 border-b border-gray-200 dark:border-gray-600">
							Materials Cost
						</div>
						{tcr.materials && tcr.materials.length > 0 ? (
							<>
								{tcr.materials.map((m, idx) => (
									<div
										key={idx}
										className="flex justify-between items-center py-1.5 pl-4"
									>
										<span className={`text-[0.9rem] ${m.skipCalculation ? 'text-gray-400 dark:text-gray-500 italic' : 'text-gray-500 dark:text-gray-400'}`}>
											{m.materialName || formatTaskType(m.materialType)}
											{m.skipCalculation ? ' (not deducted)' : ''}
										</span>
										<span className={`font-semibold ${m.skipCalculation ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
											{formatCurrency(m.price)}
										</span>
									</div>
								))}
								<div className="flex justify-between items-center pt-2 mt-1 pl-4 border-t border-gray-200 dark:border-gray-600">
									<span className="font-semibold text-gray-700 dark:text-gray-200">
										Deductible Materials
									</span>
									<span className="font-semibold text-gray-900 dark:text-gray-100">
										{formatCurrency(tcr.materialsCost)}
									</span>
								</div>
							</>
						) : (
							<p className="pl-4 py-1.5 text-gray-400 dark:text-gray-500">-</p>
						)}

						{/* Net Service Price */}
						<div className="text-[0.95rem] font-semibold text-emerald-500 dark:text-emerald-400 mt-4 mb-1 pb-2 border-b border-gray-200 dark:border-gray-600">
							Net Service Price
						</div>
						<div className="flex justify-between items-center py-1.5 pl-4">
							<span className="text-[0.9rem] text-gray-500 dark:text-gray-400">
								{formatCurrency(tcr.amount)} &minus; {formatCurrency(tcr.materialsCost)}
							</span>
							<span className="font-semibold text-gray-900 dark:text-gray-100">
								{formatCurrency(tcr.netServicePrice)}
							</span>
						</div>

						{/* Commission */}
						<div className="text-[0.95rem] font-semibold text-emerald-500 dark:text-emerald-400 mt-4 mb-1 pb-2 border-b border-gray-200 dark:border-gray-600">
							Staff Commission
						</div>
						<div className="flex justify-between items-center py-1.5 pl-4">
							<span className="text-[0.9rem] text-gray-500 dark:text-gray-400">
								{formatCurrency(tcr.netServicePrice)} &times; {tcr.commissionRate}%
							</span>
							<span className="font-semibold text-emerald-500 dark:text-emerald-400">
								{formatCurrency(tcr.commission)}
							</span>
						</div>
						{tcr.taskCompletions && tcr.taskCompletions.length > 0 && (
							<div className="pl-4 mt-1">
								{tcr.taskCompletions.map((task, idx) => (
									<p key={idx} className="text-xs text-gray-400 dark:text-gray-500">
										{formatTaskType(task.taskType)}: {Math.round(task.completionPct * 100)}% completion
									</p>
								))}
							</div>
						)}

						{/* Customer Tip */}
						<div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
							<span className="text-[0.95rem] font-semibold text-emerald-500 dark:text-emerald-400">
								Customer Tip
							</span>
							<span className="font-semibold text-gray-900 dark:text-gray-100">
								{formatCurrency(tcr.tip)}
							</span>
						</div>

						{/* Total Earnings Box */}
						<div className="bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900 dark:to-emerald-800 rounded-xl p-5 text-center mt-4 border-2 border-emerald-300 dark:border-emerald-600">
							<p className="font-semibold text-emerald-700 dark:text-emerald-300 mb-2">
								Total Earnings
							</p>
							<div className="flex justify-center gap-6 text-[0.9rem] text-emerald-600 dark:text-emerald-400 mb-2">
								<span>Commission: {formatCurrency(tcr.commission)}</span>
								<span>Tip: {formatCurrency(tcr.tip)}</span>
							</div>
							<p className="text-3xl font-bold text-emerald-500 dark:text-emerald-400">
								{formatCurrency(tcr.totalEarnings)}
							</p>
						</div>
					</div>
				</div>

				{/* Review Notes (rejected only) */}
				{tcr.status === 'REJECTED' && tcr.reviewNotes && (
					<div>
						<h3 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-3 flex items-center gap-2">
							<ExclamationTriangleIcon className="w-4 h-4" />
							Review Notes
						</h3>
						<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg">
							<p className="text-sm text-red-700 dark:text-red-300">
								{tcr.reviewNotes}
							</p>
						</div>
					</div>
				)}

				{/* Rejection Form (inline, shown when reject is clicked) */}
				{isPending && showRejectForm && (
					<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-xl">
						<label className="block font-semibold text-red-700 dark:text-red-400 mb-2 text-sm">
							Reason for Rejection *
						</label>
						<textarea
							value={rejectionNotes}
							onChange={e => setRejectionNotes(e.target.value)}
							placeholder="Please explain why this TCR is being rejected..."
							className="w-full p-3 border border-red-200 dark:border-red-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-red-400 resize-vertical min-h-[80px]"
							disabled={isRejecting}
						/>
						<div className="flex gap-3 mt-3">
							<Button
								variant="primary"
								size="sm"
								onClick={handleReject}
								isLoading={isRejecting}
								className="!bg-red-600 hover:!bg-red-700"
							>
								Confirm Rejection
							</Button>
							<Button
								variant="secondary"
								size="sm"
								onClick={() => {
									setShowRejectForm(false);
									setRejectionNotes('');
								}}
								disabled={isRejecting}
							>
								Cancel
							</Button>
						</div>
					</div>
				)}
			</div>

			{/* Footer Actions */}
			{isPending && !showRejectForm && (
				<div className="border-t border-gray-200 dark:border-gray-700 p-4 flex gap-3">
					<Button
						variant="primary"
						className="flex-1 !bg-emerald-600 hover:!bg-emerald-700"
						onClick={() => onApprove(tcr.id)}
						isLoading={isApproving}
					>
						<CheckCircleIcon className="w-4 h-4 mr-2" />
						Approve
					</Button>
					<Button
						variant="primary"
						className="flex-1 !bg-red-600 hover:!bg-red-700"
						onClick={() => setShowRejectForm(true)}
						disabled={isApproving}
					>
						<XCircleIcon className="w-4 h-4 mr-2" />
						Reject
					</Button>
				</div>
			)}
		</Modal>
	);
};

// ─── Stats Card ─────────────────────────────────────────────────

interface StatCardProps {
	value: string | number;
	label: string;
	borderColor: string;
	iconBg: string;
	icon: ReactNode;
}

const StatCard = ({ value, label, borderColor, iconBg, icon }: StatCardProps) => (
	<div className={`bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border-l-4 ${borderColor}`}>
		<div className="flex items-center justify-between">
			<div>
				<p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{label}</p>
			</div>
			<div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconBg}`}>
				{icon}
			</div>
		</div>
	</div>
);

// ─── Main TCR Approvals Page ────────────────────────────────────

const ProviderTCRApprovals = () => {
	const { selectedLocationId } = useProviderProfileStore();

	// Filter state
	const [statusFilter, setStatusFilter] = useState('ALL');
	const [staffFilter, setStaffFilter] = useState('');
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');

	// Data state
	const [data, setData] = useState<ProviderTcrResponse | null>(null);
	const [staffOptions, setStaffOptions] = useState<ProviderTcrStaffOption[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Modal state
	const [selectedTcr, setSelectedTcr] = useState<ProviderTcr | null>(null);
	const [isApproving, setIsApproving] = useState(false);
	const [isRejecting, setIsRejecting] = useState(false);

	// Debounce search
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	// Fetch TCRs
	const fetchTcrs = useCallback(async () => {
		if (!selectedLocationId) return;

		setIsLoading(true);
		setError(null);
		try {
			const response = await providerProfileApi.getTcrApprovals({
				locationId: selectedLocationId,
				status: statusFilter,
				search: debouncedSearch || undefined,
				staffId: staffFilter || undefined,
			});
			setData(response);
			// Preserve staff options from the first load (they don't change with filters)
			if (response.staffOptions?.length) {
				setStaffOptions(response.staffOptions);
			}
		} catch (err) {
			const apiError = err as ApiError;
			setError(apiError.message || 'Failed to load TCR approvals');
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId, statusFilter, debouncedSearch, staffFilter]);

	useEffect(() => {
		fetchTcrs();
	}, [fetchTcrs]);

	// Handle approve
	const handleApprove = async (id: number) => {
		setIsApproving(true);
		try {
			await providerProfileApi.approveTcr(id);
			toast.success('TCR approved successfully!');
			setSelectedTcr(null);
			fetchTcrs();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to approve TCR');
		} finally {
			setIsApproving(false);
		}
	};

	// Handle reject
	const handleReject = async (id: number, reviewNotes: string) => {
		setIsRejecting(true);
		try {
			await providerProfileApi.rejectTcr(id, reviewNotes);
			toast.success('TCR rejected. Staff has been notified.');
			setSelectedTcr(null);
			fetchTcrs();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to reject TCR');
		} finally {
			setIsRejecting(false);
		}
	};

	const tcrs = data?.tcrs ?? [];

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900 dark:text-white">TCR Approvals</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					Review and approve staff task completion reports
				</p>
			</div>

			{/* Stats Row */}
			{data && (
				<div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
					<StatCard
						value={data.pendingReview}
						label="Pending Review"
						borderColor="border-l-amber-500"
						iconBg="bg-amber-100 dark:bg-amber-900/40"
						icon={<ClockIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />}
					/>
					<StatCard
						value={data.approvedThisMonth}
						label="Approved This Month"
						borderColor="border-l-emerald-500"
						iconBg="bg-emerald-100 dark:bg-emerald-900/40"
						icon={
							<CheckCircleIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
						}
					/>
					<StatCard
						value={data.rejectedCount}
						label="Rejected"
						borderColor="border-l-red-500"
						iconBg="bg-red-100 dark:bg-red-900/40"
						icon={<XCircleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />}
					/>
					<StatCard
						value={formatCurrency(data.pendingPayout)}
						label="Pending Payout"
						borderColor="border-l-[#667eea]"
						iconBg="bg-indigo-100 dark:bg-indigo-900/40"
						icon={<DocumentTextIcon className="w-6 h-6 text-[#667eea]" />}
					/>
				</div>
			)}

			{/* Filter Bar */}
			<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-6 flex flex-wrap gap-3 items-center">
				<div className="relative flex-1 min-w-[200px]">
					<MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
					<input
						type="text"
						placeholder="Search by staff or client..."
						value={search}
						onChange={e => setSearch(e.target.value)}
						className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#667eea] transition-colors"
					/>
				</div>
				<select
					value={statusFilter}
					onChange={e => setStatusFilter(e.target.value)}
					className="px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] cursor-pointer min-w-[150px]"
				>
					{STATUS_OPTIONS.map(opt => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
				<select
					value={staffFilter}
					onChange={e => setStaffFilter(e.target.value)}
					className="px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] cursor-pointer min-w-[150px]"
				>
					<option value="">All Staff</option>
					{staffOptions.map(s => (
						<option key={s.id} value={String(s.id)}>
							{s.name}
						</option>
					))}
				</select>
			</div>

			{/* Content */}
			{isLoading && !data ? (
				<AppLoader message="Loading TCR approvals" variant="inline" />
			) : error ? (
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Unable to Load TCR Approvals"
					message={error}
					actions={[{ label: 'Retry', onClick: fetchTcrs, primary: true }]}
					onDismiss={() => setError(null)}
				/>
			) : !isLoading && tcrs.length === 0 ? (
				<div className="text-center py-16">
					<DocumentTextIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
					<h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400 mb-1">
						No reports found
					</h3>
					<p className="text-sm text-gray-400 dark:text-gray-500">
						{debouncedSearch || statusFilter !== 'ALL' || staffFilter
							? 'Try adjusting your search or filters'
							: 'Staff task completion reports will appear here'}
					</p>
				</div>
			) : (
				<>
					{/* Loading indicator for filter changes */}
					{isLoading && (
						<div className="flex justify-center py-4">
							<Spinner size="md" className="text-[#667eea]" />
						</div>
					)}

					{/* TCR Cards */}
					<div className="flex flex-col gap-4">
						{tcrs.map(tcr => {
							const dateParts = getDateParts(tcr.appointmentDateTime);
							const style = getStatusStyle(tcr.status);

							return (
								<div
									key={tcr.id}
									onClick={() => setSelectedTcr(tcr)}
									className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 cursor-pointer border-l-4 ${style.border} hover:shadow-md hover:-translate-y-0.5 transition-all`}
								>
									{/* Card Header: Staff Info + Status */}
									<div className="flex items-center justify-between mb-3">
										<div className="flex items-center gap-3">
											<div
												className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm"
												style={{
													background:
														'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
												}}
											>
												{tcr.staffInitials}
											</div>
											<div>
												<p className="font-semibold text-gray-900 dark:text-white">
													{tcr.staffName}
												</p>
												<p className="text-xs text-gray-500 dark:text-gray-400">
													{tcr.staffRole}
												</p>
											</div>
										</div>
										<span
											className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${style.badge}`}
										>
											{tcr.status}
										</span>
									</div>

									{/* Card Body */}
									<div className="flex items-center gap-4 pt-3 border-t border-gray-100 dark:border-gray-700">
										{/* Date Block */}
										<div className="text-center min-w-[60px] py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hidden sm:block">
											<p className="text-xl font-bold text-[#667eea] leading-none">
												{dateParts.day}
											</p>
											<p className="text-xs text-gray-500 dark:text-gray-400 uppercase mt-1">
												{dateParts.month}
											</p>
										</div>

										{/* Service Info */}
										<div className="flex-1 min-w-0">
											<p className="font-medium text-gray-900 dark:text-white truncate">
												{tcr.serviceName}
											</p>
											<p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
												{tcr.size ? `${tcr.size}` : ''}
												{tcr.hairLength ? ` \u00B7 ${tcr.hairLength}` : ''}
												{tcr.durationMinutes
													? ` \u00B7 ${tcr.durationMinutes} mins`
													: ''}
											</p>
											<p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
												Client: {tcr.customerName}
											</p>
											{tcr.staffAssignedTasks &&
												tcr.staffAssignedTasks.length > 0 && (
													<div className="flex flex-wrap gap-1.5 mt-2">
														{tcr.staffAssignedTasks.map((task, idx) => (
															<span
																key={idx}
																className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white"
															>
																{task}
															</span>
														))}
													</div>
												)}
										</div>

										{/* Right: Commission + Date */}
										<div className="text-right min-w-[110px] flex-shrink-0">
											<p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
												{formatCurrency(tcr.commission)}
											</p>
											<p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
												Submitted {formatDate(tcr.submittedAt)}
											</p>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</>
			)}

			{/* Detail Modal */}
			<TCRDetailModal
				tcr={selectedTcr}
				onClose={() => setSelectedTcr(null)}
				onApprove={handleApprove}
				onReject={handleReject}
				isApproving={isApproving}
				isRejecting={isRejecting}
			/>
		</AppLayout>
	);
};

export default ProviderTCRApprovals;
